import { useQueryClient } from '@tanstack/react-query';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar16,
  CheckCircle16,
  Repeat16,
  SettingsSort18,
  Sync16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { queryKey as externalCalendarEventsQueryKey } from 'shared/hooks/queries/use-external-calendar-events-query';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';

export const FilterButton: React.FC = () => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();

  const queryClient = useQueryClient();
  const refreshCalendarEvents = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [externalCalendarEventsQueryKey],
    });
    closeMenu();
  }, [closeMenu, queryClient]);

  const toggleHideHabits = () =>
    updateUserSettings({
      calendar: {
        hideHabits: !user?.settings?.calendar?.hideHabits,
      },
    });
  const toggleHideExternalCalendars = () =>
    updateUserSettings({
      calendar: {
        hideExternalCalendarEvents:
          !user?.settings?.calendar?.hideExternalCalendarEvents,
      },
    });
  const toggleHideCompletedTasks = () =>
    updateUserSettings({
      calendar: {
        hideCompletedTasks: !user?.settings?.calendar?.hideCompletedTasks,
      },
    });

  return (
    <>
      <IconButton
        icon={SettingsSort18}
        ref={buttonRef}
        onClick={openMenu}
        color={Colors.Default}
        preventDrag
      />

      {menuOpen && (
        <PopupMenu referenceElement={buttonRef} onOutside={closeMenu}>
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <PopupMenuToggle
                start={<Icon icon={CheckCircle16} />}
                label={t(
                  'pages.today.side-panel.settings.completed-tasks.show.label',
                )}
                onChange={toggleHideCompletedTasks}
                checked={!user?.settings?.calendar?.hideCompletedTasks}
              />
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuToggle
                start={<Icon icon={Repeat16} />}
                label={t('pages.today.side-panel.settings.habits.show.label')}
                onChange={toggleHideHabits}
                checked={!user?.settings?.calendar?.hideHabits}
              />
            </PopupMenuListItem>
            <PopupMenuListItem>
              <PopupMenuToggle
                start={<Icon icon={Calendar16} />}
                label={t(
                  'pages.today.side-panel.settings.external-calendars.show.label',
                )}
                onChange={toggleHideExternalCalendars}
                checked={!user?.settings?.calendar?.hideExternalCalendarEvents}
              />
            </PopupMenuListItem>
          </PopupMenuList>
          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuButton
                start={<Icon icon={Sync16} />}
                onClick={refreshCalendarEvents}
              >
                {t('pages.today.side-panel.settings.sync-calendar.label')}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
